import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

export function useWebSocket(url) {
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const seenIdsRef = useRef(new Set());

  const addNotification = useCallback((notif) => {
    if (seenIdsRef.current.has(notif.id)) return;
    seenIdsRef.current.add(notif.id);
    setNotifications(prev => [notif, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        const wsUrl = url.replace('https://', 'wss://').replace('http://', 'ws://');
        ws = new WebSocket(`${wsUrl}/ws/prices`);
        ws.onopen = () => { setConnected(true); ws.send(JSON.stringify({ ping: true })); };
        ws.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            setData(parsed);
            if (parsed.type === 'trade_executed') {
              addNotification({
                id: `trade-${parsed.timestamp}-${parsed.instrument}`,
                type: 'trade',
                title: `${parsed.agent_id} ${parsed.side}`,
                message: `${parsed.instrument} @ $${typeof parsed.price === 'number' ? parsed.price.toFixed(2) : parsed.price}`,
                timestamp: parsed.timestamp,
                severity: 'info',
              });
            } else if (parsed.type === 'pnl_milestone') {
              addNotification({
                id: `milestone-${parsed.milestone}`,
                type: 'milestone',
                title: `P&L Milestone: $${parsed.milestone?.toLocaleString()}`,
                message: `Current P&L: $${parsed.current_pnl?.toLocaleString()}`,
                timestamp: parsed.timestamp,
                severity: 'success',
              });
            } else if (parsed.type === 'agent_alert') {
              addNotification({
                id: `agent-${parsed.timestamp}-${parsed.agent_id}`,
                type: 'agent',
                title: parsed.alert_kind === 'top_performer' ? `Top: ${parsed.agent_id}` : `Alert: ${parsed.agent_id}`,
                message: `${parsed.agent_name}: ${parsed.return_pct >= 0 ? '+' : ''}${parsed.return_pct}%`,
                timestamp: parsed.timestamp,
                severity: parsed.alert_kind === 'top_performer' ? 'success' : 'warning',
              });
            } else if (parsed.type === 'cycle_complete' && parsed.trades > 0) {
              addNotification({
                id: `cycle-${parsed.cycle}`,
                type: 'cycle',
                title: `Cycle #${parsed.cycle}`,
                message: `${parsed.signals} signals, ${parsed.trades} trades, P&L: $${parsed.pnl?.toFixed(2)}`,
                timestamp: parsed.timestamp,
                severity: 'info',
              });
            }
          } catch (e) { }
        };
        ws.onclose = () => { setConnected(false); reconnectTimer = setTimeout(connect, 5000); };
        ws.onerror = () => { ws.close(); };
      } catch (e) { reconnectTimer = setTimeout(connect, 5000); }
    };
    connect();
    return () => { if (ws) ws.close(); if (reconnectTimer) clearTimeout(reconnectTimer); };
  }, [url, addNotification]);

  // Polling fallback
  useEffect(() => {
    let active = true;
    let lastCycle = 0;

    const poll = async () => {
      if (!active) return;
      try {
        const res = await axios.get(`${url}/api/notifications/recent?limit=10`, { withCredentials: true, adapter: 'xhr' });
        const notifs = res.data?.notifications || [];
        for (const n of notifs) {
          const nId = `poll-${n.type}-${n.timestamp}`;
          if (!seenIdsRef.current.has(nId)) {
            const cycleNum = parseInt(n.title?.replace('Cycle #', '') || '0');
            if (cycleNum > lastCycle) {
              lastCycle = cycleNum;
              addNotification({
                id: nId,
                type: n.type === 'cycle' ? 'cycle' : 'alert',
                title: n.title,
                message: n.message,
                timestamp: n.timestamp,
                severity: n.severity || 'info',
              });
            }
          }
        }
      } catch (e) { /* auth not ready yet */ }
    };

    const timer = setInterval(poll, 30000);
    setTimeout(poll, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [url, addNotification]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    seenIdsRef.current.clear();
  }, []);

  return { data, connected, notifications, clearNotifications };
}
